import { useEffect, useState } from "react";
import { trackVisit, trackLookup } from "@/lib/analytics";
import { lookupOrder } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import NutritionLabel from "@/components/NutritionLabel";
import { toast } from "sonner";
import { Wheat } from "lucide-react";

const inputClass =
  "rounded-md border-[#D7CCC8] bg-white px-4 py-3 text-[#5D4037] placeholder:text-[#A1887F] focus:border-[#5D4037] focus:ring-1 focus:ring-[#5D4037] transition-all";
const primaryBtn =
  "rounded-md px-6 py-3 font-medium transition-all duration-200 active:scale-95 shadow-sm hover:shadow-md bg-[#5D4037] text-white hover:bg-[#4E342E]";

// Standard blends: the order number printed on the pack is enough.
// Custom / personal blends: the server asks for the customer's last name before showing them.
const CustomerLookup = () => {
  const [orderNumber, setOrderNumber] = useState("#");
  const [lastName, setLastName] = useState("");
  const [askLastName, setAskLastName] = useState(false); // shown only when the order has a personal blend
  const [nutritionData, setNutritionData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (window.__flOwnerJustSet === "1") toast.success("This device is now marked as yours: your visits won't be counted.");
    if (window.__flOwnerJustSet === "0") toast.success("This device is counted again.");
    trackVisit("/");
  }, []);

  const showError = (error, withLastName) => {
    const code = error.response?.status;
    if (code === 429) toast.error("Too many tries. Please wait a minute and try again.");
    else if (code === 404 && withLastName) toast.error("That last name doesn't match this order. Please check the spelling.");
    else if (code === 404) toast.error("We couldn't find this order number. Please check the number printed on your pack.");
    else toast.error("Something went wrong. Please try again.");
  };

  const runLookup = async (withLastName) => {
    if (!orderNumber.replace("#", "").trim()) {
      toast.error("Please enter the order number printed on your pack");
      return;
    }
    if (withLastName && !lastName.trim()) {
      toast.error("Please enter the last name on the order");
      return;
    }
    setLoading(true);
    try {
      const data = await lookupOrder(orderNumber, withLastName ? lastName : undefined);
      trackLookup(data.items.length === 0 ? "needs_last_name" : withLastName ? "unlocked" : "found", data.items.map((i) => i.product_name));
      if (data.items.length === 0 && data.needs_last_name) {
        setAskLastName(true); // personal blend only: ask for the last name below
        setNutritionData(null);
      } else {
        setNutritionData(data);
        setAskLastName(false);
      }
    } catch (error) {
      if (error.response?.status === 404) trackLookup(withLastName ? "wrong_last_name" : "not_found");
      showError(error, withLastName);
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = (e) => {
    e.preventDefault();
    runLookup(askLastName);
  };

  const startOver = () => {
    setNutritionData(null);
    setOrderNumber("#");
    setLastName("");
    setAskLastName(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] relative overflow-hidden">
      {/* Flourma Branding - Top Bar */}
      <div className="relative z-20 bg-white border-b border-[#D7CCC8] no-print">
        <div className="container mx-auto px-4 py-4">
          <a href="https://shop.flourma.com" target="_blank" rel="noopener noreferrer" className="inline-block" data-testid="flourma-logo-link">
            <h1 className="text-3xl md:text-4xl font-bold text-[#5D4037] tracking-tight hover:text-[#4E342E] transition-colors">Flourma</h1>
          </a>
        </div>
      </div>

      {/* Background image with overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1692230335051-8cbb6a8c8a0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHx3aGVhdCUyMGZpZWxkJTIwZ29sZGVuJTIwaG91cnxlbnwwfHx8fDE3NzI2Mzg5MjV8MA&ixlib=rb-4.1.0&q=85')",
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-12 md:py-20">
        {!nutritionData ? (
          <div className="max-w-md mx-auto">
            <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl p-8 md:p-12 border border-[#D7CCC8]/30">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#5D4037] rounded-full mb-4">
                  <Wheat className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#5D4037] mb-3">View Your Nutrition Label</h1>
                <p className="text-base text-[#795548] leading-relaxed">
                  Enter the order number printed on your pack to see its complete nutrition information.
                </p>
              </div>

              <form onSubmit={handleLookup} className="space-y-6">
                <div>
                  <Label htmlFor="orderNumber" className="text-sm font-medium uppercase tracking-wider text-[#5D4037] mb-2 block">
                    Order Number
                  </Label>
                  <Input
                    id="orderNumber"
                    data-testid="order-number-input"
                    type="text"
                    placeholder="e.g. #1024"
                    value={orderNumber}
                    onChange={(e) => {
                      setOrderNumber(e.target.value);
                      setAskLastName(false);
                      setLastName("");
                    }}
                    className={inputClass}
                  />
                </div>

                {askLastName && (
                  <div data-testid="last-name-step">
                    <p className="text-sm text-[#795548] mb-3 leading-relaxed">
                      This pack is a personal blend made for one customer. To keep it private, please enter the last name on the order.
                    </p>
                    <Label htmlFor="lastName" className="text-sm font-medium uppercase tracking-wider text-[#5D4037] mb-2 block">
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      data-testid="last-name-input"
                      type="text"
                      placeholder="e.g. Gupta"
                      value={lastName}
                      autoFocus
                      onChange={(e) => setLastName(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}

                <Button data-testid="view-nutrition-button" type="submit" disabled={loading} className={"w-full " + primaryBtn}>
                  {loading ? "Searching..." : "View Nutrition Info"}
                </Button>
              </form>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-5 no-print" data-testid="order-summary">
              <p className="text-lg text-[#5D4037]">
                <span className="font-bold">Order {nutritionData.order_number}</span>
                <span className="text-[#795548]"> · for {nutritionData.customer_name}</span>
              </p>
              <p className="text-sm text-[#795548] mt-1">Not your name? Please check the number printed on your pack and search again.</p>
            </div>

            {nutritionData.items.map((item, index) => (
              <div key={item.id || `item-${index}`} className="nutrition-label-container bg-white rounded-xl shadow-2xl p-8 md:p-12">
                {nutritionData.items.length > 1 && (
                  <div className="mb-4 pb-4 border-b-2 border-[#D7CCC8] no-print">
                    <h3 className="text-2xl font-bold text-[#5D4037]">
                      Item {index + 1} of {nutritionData.items.length}
                    </h3>
                  </div>
                )}
                <NutritionLabel data={item} />
              </div>
            ))}

            {nutritionData.needs_last_name && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  runLookup(true);
                }}
                className="bg-white rounded-xl shadow-lg p-6 no-print space-y-3"
                data-testid="more-items-step"
              >
                <p className="text-[#5D4037]">
                  This order also has {nutritionData.hidden_items} personal blend{nutritionData.hidden_items > 1 ? "s" : ""}. Enter the last name on the order to see{" "}
                  {nutritionData.hidden_items > 1 ? "them" : "it"}.
                </p>
                <div className="flex gap-3">
                  <Input type="text" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClass} data-testid="more-last-name-input" />
                  <Button type="submit" disabled={loading} className={primaryBtn}>
                    {loading ? "..." : "Show"}
                  </Button>
                </div>
              </form>
            )}

            <div className="flex flex-col gap-4 no-print bg-white rounded-xl shadow-lg p-6">
              <div className="flex gap-4">
                <Button data-testid="print-label-button" onClick={() => window.print()} className={"flex-1 " + primaryBtn}>
                  Print All Labels
                </Button>
                <Button
                  data-testid="new-search-button"
                  onClick={startOver}
                  className="flex-1 rounded-md px-6 py-3 font-medium transition-all duration-200 active:scale-95 shadow-sm hover:shadow-md border-2 border-[#5D4037] text-[#5D4037] hover:bg-[#5D4037] hover:text-white bg-transparent"
                >
                  New Search
                </Button>
              </div>
              <a href="https://shop.flourma.com" target="_blank" rel="noopener noreferrer" className="w-full">
                <Button data-testid="explore-more-button" className={"w-full " + primaryBtn}>
                  Explore More at Flourma
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerLookup;
